import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader, AppIcon, PriorityPlaylist } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';
import { usePlaylistNuggets } from '@backrs_ui/grow-ui/src/hooks/usePlaylistNuggets';

import { Box, Button, SxProps, Theme } from '@mui/material';

import { useAgentContext } from './SuggestedPlaylistsSection.agentContext';
import { SectionHeader } from '../atoms/SectionHeader';
import { handlePlaylistSearch } from '../utils/playlist.utils';

const PLAYLIST_SECTION_STRINGS = appStrings.modules.priorities.suggestedPlaylistsSection;

type SuggestedPlaylistsSectionProps = Readonly<{
  playlists: PriorityPlaylist[];
  flavor?: 'blueprint' | 'priorities';
}>;

export const SuggestedPlaylistsSection = ({
  playlists,
  flavor = 'priorities',
}: SuggestedPlaylistsSectionProps) => {
  useAgentContext(playlists);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');

  const filteredPlaylists = useMemo(
    () => handlePlaylistSearch(playlists, searchTerm),
    [playlists, searchTerm],
  );

  const { playlistNuggets: filteredNuggets, handlePlaylistNuggetClick } =
    usePlaylistNuggets(filteredPlaylists);

  if (playlists.length === 0) {
    return null;
  }

  return (
    <Box sx={getContainerSx(flavor)}>
      {flavor === 'priorities' && (
        <SectionHeader
          title={PLAYLIST_SECTION_STRINGS.title}
          description={PLAYLIST_SECTION_STRINGS.description}
          searchPlaceholder={PLAYLIST_SECTION_STRINGS.searchPlaceholder}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isFilteredResultEmpty={filteredPlaylists.length === 0 && playlists.length > 0}
          disabled={playlists.length === 0}
          inline
        />
      )}

      {flavor === 'blueprint' && (
        <PageHeader
          title={PLAYLIST_SECTION_STRINGS.title}
          subtitle={PLAYLIST_SECTION_STRINGS.description}
          maxWidth={null}
          rightComponents={[
            <Button
              id="blueprint-view-more-playbooks-btn"
              key="blueprint-priorities-button"
              variant="contained"
              size="md"
              hierarchy="Secondary gray"
              sx={{ minWidth: 'fit-content' }}
              onClick={() => {
                navigate(APP_ROUTE.PRIORITIES);
              }}
            >
              <AppIcon iconName="ClipboardPlus" diameter="20px" color="Colors/Base/Black" />
              {PLAYLIST_SECTION_STRINGS.blueprintButtonText}
            </Button>,
          ]}
        />
      )}

      <NuggetCardList
        nuggets={filteredNuggets}
        onClick={handlePlaylistNuggetClick}
        direction="scrollingRow"
        hideActions
      />
    </Box>
  );
};

const getContainerSx =
  (flavor?: 'blueprint' | 'priorities'): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-2xl'],
    maxWidth: flavor === 'blueprint' ? 'unset' : '1240px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    px: backrs.spacing['spacing-none'],
    '@container (min-width: 600px)': {
      gap: backrs.spacing['spacing-3xl'],
    },
  });
